
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../integrations/supabase/client';
import type { Database } from '../../../integrations/supabase/types';
import { useState } from 'react';
import { Search, Loader2, AlertCircle } from 'lucide-react';

type Profile = Database['public']['Tables']['profiles']['Row'];

const Customers = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: profiles, isLoading, error } = useQuery({
    queryKey: ['profiles'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching profiles:', error);
          throw error;
        }
        return data as Profile[];
      } catch (err) {
        console.error('Failed to fetch profiles:', err);
        throw err;
      }
    }
  });

  // Filter profiles by email, ensuring we handle undefined profiles array
  const filteredProfiles = profiles?.filter(profile => 
    profile.email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (isLoading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
      <span className="ml-2">Loading customers...</span>
    </div>
  );
  
  if (error) return (
    <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6">
      <div className="flex items-center">
        <AlertCircle className="w-5 h-5 mr-2" />
        <h3 className="text-lg font-medium">Error loading customers</h3>
      </div>
      <p className="mt-1">There was a problem fetching customer data. Please try again later.</p>
      <p className="mt-2 text-sm font-mono bg-red-100 p-2 rounded">
        {error instanceof Error ? error.message : 'Unknown error'}
      </p>
    </div>
  );

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Manage Customers</h2>
      
      {/* Search bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="w-5 h-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="w-full p-2 pl-10 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            placeholder="Search customers by email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProfiles.length > 0 ? (
                filteredProfiles.map((profile: Profile) => (
                  <tr key={profile.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {profile.id.substring(0, 8)}...
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{profile.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{profile.full_name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(profile.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                    {searchTerm ? 'No customers found matching your search.' : 'No customers found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Customers;
